/**
 * StepDesign — step 3 of the agent-creation flow: make the widget yours.
 *
 * Own component. LEFT: the same controls, markup and styling as Deploy → Design
 * (same accordions, tooltips, fields, i18n keys and widget_configuration
 * contract — GET / PUT, image uploads via upload_image/, crop via the portal's
 * CroppingModal), minus the portal-only pre-chat gate. RIGHT: a lean, static
 * rendering of the launcher + the opened chat interface — no site screenshot,
 * no open/close, no background — always at the widget's real proportions
 * (window 430 × 620), scaled to fill the preview column to the top and the
 * right, the launcher sized along with it.
 *
 * Continue saves; leaving the step any other way (chips, Save & exit) saves
 * through the page's before-leave hook. Removing "Powered by" is plan-gated
 * (Boost+): on Free / Smart the switch opens the plan modal.
 */
import { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  TextField, Accordion, AccordionSummary, AccordionDetails, Box, InputAdornment, IconButton,
  Typography, FormControlLabel, Switch, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faPlus, faTrash, faPen, faArrowUpFromBracket, faArrowsLeftRight, faArrowsUpDown, faEllipsis, faXmark, faArrowUp, faHeadset } from '@fortawesome/free-solid-svg-icons';
import WebFont from 'webfontloader';
import InfoTooltip from '../../../components/InfoTooltip.js';
import CroppingModal from '../../../components/croppingModal.js';
import PoweredByUpgradeModal from '../../../components/poweredByUpgradeModal.js';
import { FONT_OPTIONS, DEFAULT_FONT, findFontConfig } from '../../../constants/fonts';
import { API, planName } from '../steps.js';
import PlanModal from '../PlanModal.js';
import StepFooter from './StepFooter.js';
import '../../../components/widgetCustomizationTab.css';
import '../../../components/notificationCenter.css';
import './StepDesign.css';

const MAX_HEADER = 60;
const MAX_WELCOME = 300;
const MAX_QUESTION = 40;
const MAX_PLACEHOLDER = 40;

// Real widget footprint (the widget's own CSS): window 430 × 620.
const WIN_W = 430;
const WIN_H = 620;

// ── same sx as the Design page ──
const ACCORDION_SX = {
  background: 'transparent', boxShadow: 'none', border: 'none', borderRadius: 0,
  '&:before': { display: 'none' },
  marginTop: 0, marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #e0e0e0',
  '&.Mui-expanded': { marginTop: 0, marginBottom: '16px' },
  '&.widget-accordion-last': { borderBottom: 'none', marginBottom: 0, paddingBottom: 0, '&.Mui-expanded': { marginBottom: 0 } },
};
const ACCORDION_SUMMARY_SX = {
  padding: 0, minHeight: 'unset', transition: 'margin-bottom 0.3s ease',
  '& .MuiAccordionSummary-content': { margin: 0, '&.Mui-expanded': { margin: 0 } },
  '&.Mui-expanded': { minHeight: 'unset', marginBottom: '10px' },
  '& .MuiAccordionSummary-expandIconWrapper': { color: '#5616ea', fontSize: '18px' },
};
const ACCORDION_DETAILS_SX = { padding: 0, paddingTop: '8px' };
const FIELD_SX = {
  '& .MuiOutlinedInput-root.Mui-focused fieldset': { border: '1px solid #515151' },
  '& .MuiInputLabel-root': { color: 'gray', '&.Mui-focused': { color: '#5616ea' } },
};
const SWITCH_SX = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#5616ea' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5616ea' },
  '& .MuiSwitch-track': { backgroundColor: '#ccc' },
};
const helperSx = (len, max) => ({ sx: { textAlign: 'right', mr: 0, color: len >= max ? 'error.main' : 'text.secondary' } });

const DEFAULTS = {
  themeColor: '#000000', headerColor: '#ffffff', hoverColor: '#232323', pulsing: false,
  width: 400, height: 550, icon: false, logo: false, avatar: false,
  welcomeMessages: ['Hi, how can I assist you?'], predefinedQuestions: [], popUpMessagesActive: false, popUpDelaySeconds: 5,
  widgetBorderRadius: 50, fontFamily: DEFAULT_FONT.value, widgetSize: 60,
  widgetHorizontalAlignment: 'right', widgetVerticalAlignment: 'bottom',
  headerText: 'Chat with us', inputPlaceholder: 'Type your message...',
  removePoweredBy: false, customBrandingText: '', openLinksInNewTab: true,
};

// <input type="color"> wants #rrggbb; the backend may store #rrggbbaa.
const hex6 = (v, fallback) => (typeof v === 'string' && /^#[0-9a-f]{6}/i.test(v) ? v.slice(0, 7) : fallback);

// Server → local (mirrors the Design page's mapping).
function fromServer(d) {
  const pq = d.predefined_questions;
  return {
    ...DEFAULTS,
    themeColor: hex6(d.theme_color, DEFAULTS.themeColor),
    hoverColor: hex6(d.button_hover_color, DEFAULTS.hoverColor),
    pulsing: !!d.pulsing,
    headerColor: hex6(d.header_font_color, DEFAULTS.headerColor),
    welcomeMessages: d.welcome_message ? (Array.isArray(d.welcome_message) ? d.welcome_message : [d.welcome_message]) : [''],
    predefinedQuestions: !pq ? [] : Array.isArray(pq) ? (pq.length === 1 && pq[0] === '' ? [] : pq) : (pq === '' ? [] : [pq]),
    popUpMessagesActive: d.pop_up_messages ?? false,
    popUpDelaySeconds: d.pop_up_delay_seconds ?? 5,
    widgetBorderRadius: d.widget_border_radius ?? 50,
    widgetSize: d.widget_size ?? 60,
    icon: d.widget_icon_path || false,
    logo: d.header_icon_path || false,
    avatar: d.avatar_icon_path || false,
    widgetHorizontalAlignment: d.widget_horizontal_alignment || 'right',
    widgetVerticalAlignment: d.widget_vertical_alignment || 'bottom',
    headerText: d.header_text ?? DEFAULTS.headerText,
    fontFamily: d.font_family || DEFAULT_FONT.value,
    inputPlaceholder: d.input_placeholder || DEFAULTS.inputPlaceholder,
    removePoweredBy: d.remove_powered_by ?? false,
    customBrandingText: d.custom_branding_text ?? '',
    openLinksInNewTab: d.open_links_in_new_tab ?? true,
  };
}

function base64ToFile(base64, filename) {
  const arr = base64.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8 = new Uint8Array(n);
  while (n--) u8[n] = bstr.charCodeAt(n);
  return new File([u8], filename, { type: mime });
}

const resolveImageSrc = (value) => {
  if (!value || typeof value !== 'string') return null;
  if (value.startsWith('data:') || /^https?:\/\//i.test(value)) return value;
  return `${API}${value.startsWith('/') ? value.slice(1) : value}`;
};

const DEFAULT_LAUNCHER = (
  <svg xmlns="http://www.w3.org/2000/svg" width="65%" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" d="M12 2.25c-2.429 0-4.817.178-7.152.521C2.87 3.061 1.5 4.795 1.5 6.741v6.018c0 1.946 1.37 3.68 3.348 3.97.877.129 1.761.234 2.652.316V21a.75.75 0 0 0 1.28.53l4.184-4.183a.39.39 0 0 1 .266-.112c2.006-.05 3.982-.22 5.922-.506 1.978-.29 3.348-2.023 3.348-3.97V6.741c0-1.947-1.37-3.68-3.348-3.97A49.145 49.145 0 0 0 12 2.25ZM8.25 8.625a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Zm2.625 1.125a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875-1.125a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Z" clipRule="evenodd" />
  </svg>
);

/* ── Static preview: launcher (bottom-left) + opened chat window, real proportions,
   scaled to fill the stage to the top and to the right. ── */
function ChatPreview({ s }) {
  const { t } = useTranslation();
  const stageRef = useRef(null);
  const bodyRef = useRef(null);
  const replyTimer = useRef(null);
  const [scale, setScale] = useState(1);
  const [chat, setChat] = useState([]);       // [{ role: 'user' | 'bot', text }]
  const [typing, setTyping] = useState(false);
  const [draft, setDraft] = useState('');
  const [liveOpen, setLiveOpen] = useState(false);
  const size = Number(s.widgetSize) || 60;

  useEffect(() => () => { if (replyTimer.current) clearTimeout(replyTimer.current); }, []);
  useEffect(() => {
    // keep the newest message in view
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat, typing]);

  // A dummy conversation: the visitor's message appears, then a canned reply
  // that points to the Test step. The real agent answers two steps from here.
  const send = (text) => {
    const msg = (text || '').trim();
    if (!msg || typing) return;
    setDraft('');
    setChat((c) => [...c, { role: 'user', text: msg }]);
    setTyping(true);
    replyTimer.current = setTimeout(() => {
      setTyping(false);
      setChat((c) => [...c, { role: 'bot', text: t('ac_design_pv_reply') }]);
    }, 900);
  };

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => {
      const W = el.clientWidth; const H = el.clientHeight;
      if (!W || !H) return;
      const gap = 22; const pad = 12; // pad = room for the shadows
      // window height fills the stage; width must leave room for the launcher + gap
      const k = Math.min((H - pad) / WIN_H, (W - pad) / (WIN_W + size + gap));
      setScale(Math.max(0.2, Math.min(1.6, k)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [size]);

  const iconSrc = resolveImageSrc(s.icon);
  const logoSrc = resolveImageSrc(s.logo);
  const avatarSrc = resolveImageSrc(s.avatar);
  const msgs = (s.welcomeMessages || []).filter((m) => m && m.trim().length) ;
  const qs = (s.predefinedQuestions || []).filter((q) => q && q.trim().length);
  const font = s.fontFamily || DEFAULT_FONT.value;

  return (
    <div className="ac-pv-stage" ref={stageRef}>
      <div className="ac-pv-row" style={{ gap: `${22 * scale}px` }}>
        <div className={`ac-pv-bubble ${s.pulsing ? 'is-pulsing' : ''}`}
          style={{ width: size * scale, height: size * scale, borderRadius: `${s.widgetBorderRadius}%`, background: iconSrc ? 'transparent' : s.themeColor, fontFamily: font }}>
          {iconSrc ? <img src={iconSrc} alt="" style={{ borderRadius: `${s.widgetBorderRadius}%` }} /> : DEFAULT_LAUNCHER}
        </div>
        <div className="ac-pv-window-box" style={{ width: WIN_W * scale, height: WIN_H * scale }}>
          <div className="ac-pv-window" style={{ width: WIN_W, height: WIN_H, transform: `scale(${scale})`, fontFamily: font }}>
            <div className="ac-pv-header" style={{ background: s.themeColor, color: s.headerColor }}>
              <span className="ac-pv-brand">
                {logoSrc && <img className="ac-pv-logo" src={logoSrc} alt="" />}
                <span className="ac-pv-title">{s.headerText || 'Chat with us'}</span>
              </span>
              <span className="ac-pv-actions"><FontAwesomeIcon icon={faEllipsis} /><FontAwesomeIcon icon={faXmark} /></span>
            </div>
            <div className="ac-pv-body" ref={bodyRef}>
              {(msgs.length ? msgs : [t('ac_design_pv_default_welcome')]).map((m, i) => (
                <div key={`w${i}`} className="ac-pv-msg-row">
                  {avatarSrc && <img className="ac-pv-avatar" src={avatarSrc} alt="" />}
                  <div className="ac-pv-msg">{m}</div>
                </div>
              ))}
              {chat.map((m, i) => (
                m.role === 'user' ? (
                  <div key={`c${i}`} className="ac-pv-msg-row is-user">
                    <div className="ac-pv-msg ac-pv-msg-user" style={{ background: s.themeColor, color: s.headerColor }}>{m.text}</div>
                  </div>
                ) : (
                  <div key={`c${i}`} className="ac-pv-msg-row">
                    {avatarSrc && <img className="ac-pv-avatar" src={avatarSrc} alt="" />}
                    <div className="ac-pv-msg">{m.text}</div>
                  </div>
                )
              ))}
              {typing && (
                <div className="ac-pv-msg-row">
                  {avatarSrc && <img className="ac-pv-avatar" src={avatarSrc} alt="" />}
                  <div className="ac-pv-msg ac-pv-typing" style={{ '--pv-dot': s.themeColor }}><span /><span /><span /></div>
                </div>
              )}
            </div>
            <div className="ac-pv-footer">
              {qs.length > 0 && (
                <div className="ac-pv-chips">
                  {qs.map((q, i) => <button key={i} type="button" className="ac-pv-chip" onClick={() => send(q)}>{q}</button>)}
                </div>
              )}
              {/* Same "Talk to a human" pill the real widget shows (live chat) */}
              <div className="ac-pv-live-row">
                <button type="button" className="ac-pv-live-btn" onClick={() => setLiveOpen(true)}>
                  <FontAwesomeIcon icon={faHeadset} /><span>{t('wxo_design_menu_human')}</span>
                </button>
              </div>
              {(!s.removePoweredBy || s.customBrandingText) && (
                <div className="ac-pv-powered">{s.removePoweredBy ? s.customBrandingText : 'Powered by Ultimo Bots'}</div>
              )}
              <div className="ac-pv-input-row">
                <input
                  className="ac-pv-input"
                  type="text"
                  value={draft}
                  placeholder={s.inputPlaceholder || DEFAULTS.inputPlaceholder}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(draft); } }}
                />
                <button type="button" className="ac-pv-send" style={{ background: s.themeColor, color: s.headerColor }} onClick={() => send(draft)} disabled={!draft.trim() || typing} aria-label={t('ac_design_pv_send')}>
                  <FontAwesomeIcon icon={faArrowUp} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {liveOpen && createPortal(
        <div className="ac-live-overlay" onClick={() => setLiveOpen(false)}>
          <div className="ac-live-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="ac-live-grid" aria-hidden="true" />
            <button type="button" className="ac-live-close" onClick={() => setLiveOpen(false)} aria-label={t('close')}><FontAwesomeIcon icon={faXmark} /></button>
            <div className="ac-live-inner">
              <span className="ac-live-icon"><FontAwesomeIcon icon={faHeadset} /></span>
              <h3 className="ac-live-title">{t('wxo_live_modal_title')}</h3>
              <p className="ac-live-body">{t('wxo_live_modal_body')}</p>
              <button type="button" className="ac-live-ok" onClick={() => setLiveOpen(false)}>{t('wxo_live_modal_ok')}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default function StepDesign({ agent, goNext, goBack, registerBeforeLeave, notify, returnPath, strips, isWebflow }) {
  const { t } = useTranslation();
  const botId = agent?.id;
  const [styles, setStyles] = useState(DEFAULTS);
  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [planOpen, setPlanOpen] = useState(false);
  const [brandingInfoOpen, setBrandingInfoOpen] = useState(false); // "Premium feature" info first, then the plan modal
  const [expanded, setExpanded] = useState({ headerText: true, welcomeMessages: true, popUps: true, predefinedQuestions: true, inputPlaceholder: true, colors: true, branding: true, widget: true, linkBehavior: true });
  const toggle = (k) => (_e, open) => setExpanded((p) => ({ ...p, [k]: open }));
  // crop flow (same as the Design page)
  const [cropTarget, setCropTarget] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropInitial, setCropInitial] = useState(null);
  const [uploadedNames, setUploadedNames] = useState({ icon: null, logo: null, avatar: null });
  const originalsRef = useRef({ icon: null, logo: null, avatar: null });
  const lastCropRef = useRef({ icon: null, logo: null, avatar: null });
  const inputRefs = { icon: useRef(null), logo: useRef(null), avatar: useRef(null) };
  const stylesRef = useRef(styles); stylesRef.current = styles;
  const initialRef = useRef(initial); initialRef.current = initial;

  const set = (k, v) => setStyles((p) => ({ ...p, [k]: v }));

  // ── load ──
  const load = useCallback(async () => {
    if (!botId) return;
    // Raw fetch: the config can be a beat behind the instant create (404) and
    // the global interceptor would pop its error modal on that.
    const raw = window._originalFetch || fetch;
    for (let i = 0; i < 6; i += 1) {
      try {
        const r = await raw(`${API}widget_configuration/${encodeURIComponent(botId)}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        if (r.ok) {
          const d = await r.json();
          const mapped = fromServer(d);
          setStyles(mapped);
          setInitial((prev) => prev || mapped);
          setLoading(false);
          return;
        }
      } catch { /* retry */ }
      await new Promise((res) => setTimeout(res, 800));
    }
    setLoading(false);
  }, [botId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const cfg = findFontConfig(styles.fontFamily);
    if (cfg?.google) WebFont.load({ google: { families: [cfg.google] } });
  }, [styles.fontFamily]);

  // ── save (Continue + before-leave) ──
  const uploadImage = async (base64, type) => {
    const fd = new FormData();
    fd.append('file', base64ToFile(base64, `${type}-${Date.now()}.png`));
    const res = await fetch(`${API}upload_image/`, { method: 'POST', body: fd });
    if (!res.ok) throw new Error(`upload ${type} ${res.status}`);
    const j = await res.json();
    return j.file_path;
  };

  const save = useCallback(async () => {
    const s = stylesRef.current;
    let icon = s.icon, logo = s.logo, avatar = s.avatar;
    if (icon && String(icon).startsWith('data:image/')) icon = await uploadImage(icon, 'icon');
    if (logo && String(logo).startsWith('data:image/')) logo = await uploadImage(logo, 'logo');
    if (avatar && String(avatar).startsWith('data:image/')) avatar = await uploadImage(avatar, 'avatar');
    const questions = s.predefinedQuestions.filter((q) => q.trim().length);
    const payload = {
      theme_color: s.themeColor,
      button_hover_color: s.hoverColor,
      pulsing: s.pulsing,
      chat_window_width: s.width,
      chat_window_height: s.height,
      header_font_color: s.headerColor,
      welcome_message: s.welcomeMessages,
      predefined_questions: questions.length ? questions : [''],
      pop_up_messages: s.popUpMessagesActive,
      pop_up_delay_seconds: s.popUpDelaySeconds,
      widget_horizontal_alignment: s.widgetHorizontalAlignment,
      widget_vertical_alignment: s.widgetVerticalAlignment,
      widget_border_radius: s.widgetBorderRadius,
      widget_size: s.widgetSize,
      widget_icon_path: icon || '',
      header_icon_path: logo || '',
      avatar_icon_path: avatar || '',
      header_text: s.headerText,
      font_family: s.fontFamily,
      input_placeholder: s.inputPlaceholder || DEFAULTS.inputPlaceholder,
      remove_powered_by: s.removePoweredBy,
      custom_branding_text: s.customBrandingText || '',
      open_links_in_new_tab: s.openLinksInNewTab,
    };
    const res = await fetch(`${API}widget_configuration/${encodeURIComponent(botId)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (!res.ok) throw new Error(`save ${res.status}`);
    const next = { ...s, icon: icon || false, logo: logo || false, avatar: avatar || false };
    setStyles(next); setInitial(next);
  }, [botId]);

  const saveIfDirty = useCallback(async () => {
    if (!initialRef.current) return true;
    if (JSON.stringify(stylesRef.current) === JSON.stringify(initialRef.current)) return true;
    await save();
    return true;
  }, [save]);

  useEffect(() => {
    registerBeforeLeave(saveIfDirty);
    return () => registerBeforeLeave(null);
  }, [registerBeforeLeave, saveIfDirty]);

  const continueNext = async () => {
    setSaving(true); setErr('');
    try {
      const wasDirty = initialRef.current && JSON.stringify(stylesRef.current) !== JSON.stringify(initialRef.current);
      await saveIfDirty();
      registerBeforeLeave(null);
      if (wasDirty) notify(t('ac_design_saved'));
      goNext();
    } catch (e) { console.error(e); setErr(t('ac_design_save_failed')); }
    finally { setSaving(false); }
  };

  // ── images: upload → crop → slot (same flow as the Design page) ──
  const onPick = (type) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    lastCropRef.current[type] = null;
    setUploadedNames((p) => ({ ...p, [type]: file.name }));
    const reader = new FileReader();
    reader.onload = () => { originalsRef.current[type] = reader.result; setCropInitial(null); setCropSrc(reader.result); setCropTarget(type); };
    reader.readAsDataURL(file);
  };
  const editImage = (type) => {
    const src = originalsRef.current[type] || resolveImageSrc(styles[type]);
    if (!src) return;
    setCropInitial(lastCropRef.current[type] || null); setCropSrc(src); setCropTarget(type);
  };
  const removeImage = (type) => {
    originalsRef.current[type] = null; lastCropRef.current[type] = null;
    setUploadedNames((p) => ({ ...p, [type]: null }));
    set(type, false);
    if (inputRefs[type].current) inputRefs[type].current.value = null;
  };
  const cancelCrop = () => { setCropTarget(null); setCropSrc(null); setCropInitial(null); Object.values(inputRefs).forEach((r) => { if (r.current) r.current.value = null; }); };
  const confirmCrop = (base64, percentCrop) => {
    if (cropTarget) { lastCropRef.current[cropTarget] = percentCrop || null; set(cropTarget, base64); }
    cancelCrop();
  };

  const plan = planName(agent);
  const brandingAllowed = plan === 'Boost' || plan === 'Ultimo';
  const fontCfg = findFontConfig(styles.fontFamily);
  const fontOptions = useMemo(() => (styles.fontFamily && !fontCfg ? [...FONT_OPTIONS, { label: `${styles.fontFamily} (custom)`, value: styles.fontFamily }] : FONT_OPTIONS), [styles.fontFamily, fontCfg]);

  const summary = (labelKey, tipKey) => (
    <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />} sx={ACCORDION_SUMMARY_SX}>
      <h4 className="widget-sub-header" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>
        {t(labelKey)}
        <InfoTooltip text={t(tipKey)} />
      </h4>
    </AccordionSummary>
  );

  const uploadSlot = (type, labelKey, tipKey, existingKey) => (
    <div className="control-group">
      <div className="upload-with-tooltip">
        <label className="file-upload">
          <div className="upload-icon-text">
            <FontAwesomeIcon icon={faArrowUpFromBracket} />
            <p>{t(labelKey)}</p>
          </div>
          <input type="file" accept="image/*" ref={inputRefs[type]} onChange={onPick(type)} />
        </label>
        <InfoTooltip text={t(tipKey)} />
      </div>
      {styles[type] && (
        <div className="uploaded-file-info">
          <span>{uploadedNames[type] || t(existingKey)}</span>
          {typeof styles[type] === 'string' && styles[type].startsWith('data:') && (
            <button onClick={() => editImage(type)} className="remove-file-btn" title={t('widget_edit_recrop')}><FontAwesomeIcon icon={faPen} /></button>
          )}
          <button onClick={() => removeImage(type)} className="remove-file-btn" title={t('widget_remove_file')}><FontAwesomeIcon icon={faTrash} /></button>
        </div>
      )}
    </div>
  );

  const radio = (id, name, checked, onChange, labelKey) => (
    <div className="radio-buttons-content">
      <input type="radio" id={id} name={name} checked={checked} onChange={onChange} />
      <label htmlFor={id}>{t(labelKey)}</label>
    </div>
  );

  return (
    <>
      <header className="ac-head">
        <h2 className="ac-title">{t('ac_design_title')}</h2>
        <p className="ac-sub">{t('ac_design_sub')}</p>
      </header>

      <div className="ac-body ac-body-design">
        {loading ? (
          <div className="ac-loading"><span className="ac-spinner" /></div>
        ) : (
          <div className="ac-design">
            {/* ── LEFT: the Design page's controls ── */}
            <div className="ac-design-controls">
              <div className="options-container ac-options">
                {/* header text */}
                <Accordion expanded={!!expanded.headerText} onChange={toggle('headerText')} disableGutters elevation={0} sx={ACCORDION_SX}>
                  {summary('header_text', 'widget_header_text_tooltip')}
                  <AccordionDetails sx={ACCORDION_DETAILS_SX}>
                    <div className="control-group" style={{ marginBottom: '0' }}>
                      <TextField id="header-text-input" label={t('edit_header_text')} value={styles.headerText} onChange={(e) => set('headerText', e.target.value)}
                        multiline variant="outlined" inputProps={{ maxLength: MAX_HEADER }} helperText={`${styles.headerText.length}/${MAX_HEADER}`}
                        FormHelperTextProps={helperSx(styles.headerText.length, MAX_HEADER)} sx={FIELD_SX} />
                    </div>
                  </AccordionDetails>
                </Accordion>

                {/* welcome messages */}
                <Accordion expanded={!!expanded.welcomeMessages} onChange={toggle('welcomeMessages')} disableGutters elevation={0} sx={ACCORDION_SX}>
                  {summary('edit_welcome_messages', 'widget_welcome_messages_tooltip')}
                  <AccordionDetails sx={ACCORDION_DETAILS_SX}>
                    <div className="control-group">
                      <div className="welcome-messages-parent">
                        {styles.welcomeMessages.map((msg, idx) => (
                          <Box key={idx} marginBottom={-0.8}>
                            <TextField label={`${t('welcome_message')} ${idx + 1}`} value={msg}
                              onChange={(e) => { const a = [...styles.welcomeMessages]; a[idx] = e.target.value; set('welcomeMessages', a); }}
                              fullWidth multiline variant="outlined" inputProps={{ maxLength: MAX_WELCOME }} helperText={`${msg.length}/${MAX_WELCOME}`}
                              FormHelperTextProps={helperSx(msg.length, MAX_WELCOME)}
                              InputProps={{ endAdornment: styles.welcomeMessages.length > 1 ? (
                                <InputAdornment position="end"><IconButton aria-label={t('remove')} onClick={() => { const a = styles.welcomeMessages.filter((_, i) => i !== idx); set('welcomeMessages', a.length ? a : ['']); }} edge="end" size="small" sx={{ color: 'text.secondary' }}><FontAwesomeIcon icon={faTrash} /></IconButton></InputAdornment>
                              ) : null }}
                              sx={FIELD_SX} />
                          </Box>
                        ))}
                      </div>
                      <button onClick={() => set('welcomeMessages', [...styles.welcomeMessages, ''])} className="add-email-button" title={t('add')} disabled={styles.welcomeMessages.length >= 3}><FontAwesomeIcon icon={faPlus} /></button>
                      {styles.welcomeMessages.length >= 3 && <Typography variant="caption" color="textSecondary">{t('max_three_reached')}</Typography>}
                    </div>
                  </AccordionDetails>
                </Accordion>

                {/* attention pop-ups */}
                <Accordion expanded={!!expanded.popUps} onChange={toggle('popUps')} disableGutters elevation={0} sx={ACCORDION_SX}>
                  {summary('attention_pop_ups', 'widget_pop_ups_tooltip')}
                  <AccordionDetails sx={ACCORDION_DETAILS_SX}>
                    <div className="control-group">
                      <div className="delay-seconds-container">
                        <FormControlLabel control={<Switch checked={!!styles.popUpMessagesActive} onChange={(e) => set('popUpMessagesActive', e.target.checked)} sx={SWITCH_SX} />} label={t('activate_pop_up_messages')} />
                        {styles.popUpMessagesActive && (
                          <TextField label={t('delay_seconds')} type="number" className="delay-seconds-input" value={styles.popUpDelaySeconds}
                            onChange={(e) => set('popUpDelaySeconds', parseInt(e.target.value, 10) || 0)} variant="outlined" inputProps={{ style: { padding: '8px 10px' } }} sx={FIELD_SX} />
                        )}
                      </div>
                      {/* The hosted Webflow widget leaves timed pop-ups out (Webflow guideline). */}
                      {isWebflow && <p className="widget-pop-ups-webflow-note">{t('widget_pop_ups_webflow_note')}</p>}
                    </div>
                  </AccordionDetails>
                </Accordion>

                {/* predefined questions */}
                <Accordion expanded={!!expanded.predefinedQuestions} onChange={toggle('predefinedQuestions')} disableGutters elevation={0} sx={ACCORDION_SX}>
                  {summary('edit_predefined_questions', 'widget_predefined_questions_tooltip')}
                  <AccordionDetails sx={ACCORDION_DETAILS_SX}>
                    <div className="control-group">
                      <div className="welcome-messages-parent">
                        {styles.predefinedQuestions.map((q, idx) => (
                          <Box key={idx} marginBottom={-0.8}>
                            <TextField label={`${t('predefined_question')} ${idx + 1}`} value={q}
                              onChange={(e) => { const a = [...styles.predefinedQuestions]; a[idx] = e.target.value; set('predefinedQuestions', a); }}
                              fullWidth multiline variant="outlined" inputProps={{ maxLength: MAX_QUESTION }} helperText={`${q.length}/${MAX_QUESTION}`}
                              FormHelperTextProps={helperSx(q.length, MAX_QUESTION)}
                              InputProps={{ endAdornment: (
                                <InputAdornment position="end"><IconButton aria-label={t('remove')} onClick={() => set('predefinedQuestions', styles.predefinedQuestions.filter((_, i) => i !== idx))} edge="end" size="small" sx={{ color: 'text.secondary' }}><FontAwesomeIcon icon={faTrash} /></IconButton></InputAdornment>
                              ) }}
                              sx={FIELD_SX} />
                          </Box>
                        ))}
                      </div>
                      <button onClick={() => { if (styles.predefinedQuestions.length < 3) set('predefinedQuestions', [...styles.predefinedQuestions, '']); }} className="add-email-button" title={t('add')} disabled={styles.predefinedQuestions.length >= 3}><FontAwesomeIcon icon={faPlus} /></button>
                      {styles.predefinedQuestions.length >= 3 && <Typography variant="caption" color="textSecondary">{t('max_three_reached')}</Typography>}
                    </div>
                  </AccordionDetails>
                </Accordion>

                {/* input placeholder */}
                <Accordion expanded={!!expanded.inputPlaceholder} onChange={toggle('inputPlaceholder')} disableGutters elevation={0} sx={ACCORDION_SX}>
                  {summary('input_placeholder', 'widget_input_placeholder_tooltip')}
                  <AccordionDetails sx={ACCORDION_DETAILS_SX}>
                    <div className="control-group" style={{ marginBottom: '0' }}>
                      <TextField label={t('edit_input_placeholder')} value={styles.inputPlaceholder} onChange={(e) => set('inputPlaceholder', e.target.value)}
                        variant="outlined" inputProps={{ maxLength: MAX_PLACEHOLDER }} helperText={`${styles.inputPlaceholder.length}/${MAX_PLACEHOLDER}`}
                        FormHelperTextProps={helperSx(styles.inputPlaceholder.length, MAX_PLACEHOLDER)} sx={FIELD_SX} />
                    </div>
                  </AccordionDetails>
                </Accordion>

                {/* colours */}
                <Accordion expanded={!!expanded.colors} onChange={toggle('colors')} disableGutters elevation={0} sx={ACCORDION_SX}>
                  {summary('colors', 'widget_colors_tooltip')}
                  <AccordionDetails sx={ACCORDION_DETAILS_SX}>
                    <div className="control-group"><div className="color-picker-container">
                      <label className="widget-setting-title" htmlFor="themeColorPicker">{t('widget_theme_color_label')}</label>
                      <input id="themeColorPicker" type="color" value={styles.themeColor} onChange={(e) => set('themeColor', e.target.value)} />
                    </div></div>
                    <div className="control-group"><div className="color-picker-container">
                      <label className="widget-setting-title">{t('widget_header_font_color_label')}</label>
                      <input type="color" value={styles.headerColor} onChange={(e) => set('headerColor', e.target.value)} />
                    </div></div>
                    <div className="control-group"><div className="color-picker-container">
                      <label className="widget-setting-title">{t('widget_button_hover_color_label')}</label>
                      <input type="color" value={styles.hoverColor} onChange={(e) => set('hoverColor', e.target.value)} />
                    </div></div>
                  </AccordionDetails>
                </Accordion>

                {/* branding */}
                <Accordion expanded={!!expanded.branding} onChange={toggle('branding')} disableGutters elevation={0} sx={ACCORDION_SX}>
                  {summary('branding', 'widget_branding_tooltip')}
                  <AccordionDetails sx={ACCORDION_DETAILS_SX}>
                    <div className="control-group">
                      <FormControlLabel control={<Switch checked={!!styles.removePoweredBy} onChange={(e) => { if (!brandingAllowed) { setBrandingInfoOpen(true); return; } set('removePoweredBy', e.target.checked); }} sx={SWITCH_SX} />} label={t('widget_remove_powered_by_label')} />
                    </div>
                    {styles.removePoweredBy && (
                      <div className="control-group" style={{ marginTop: 12 }}>
                        <label htmlFor="customBrandingTextInput" style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                          {t('widget_custom_branding_text_label')}<InfoTooltip text={t('widget_custom_branding_text_tooltip')} />
                        </label>
                        <TextField id="customBrandingTextInput" size="small" fullWidth value={styles.customBrandingText || ''}
                          onChange={(e) => { if (!brandingAllowed) { setBrandingInfoOpen(true); return; } set('customBrandingText', e.target.value); }}
                          placeholder={t('widget_custom_branding_text_ph')} inputProps={{ maxLength: 80 }} helperText={`${(styles.customBrandingText || '').length}/80`} />
                      </div>
                    )}
                  </AccordionDetails>
                </Accordion>

                {/* widget: alignment, pulsing, size, radius, images, font */}
                <Accordion expanded={!!expanded.widget} onChange={toggle('widget')} disableGutters elevation={0} sx={ACCORDION_SX}>
                  {summary('widget', 'widget_appearance_tooltip')}
                  <AccordionDetails sx={ACCORDION_DETAILS_SX}>
                    <div className="control-group"><div className="color-picker-container">
                      <label className="widget-setting-title">{t('widget_horizontal_alignment')}<FontAwesomeIcon icon={faArrowsLeftRight} className="alignment-icon" /></label>
                      <div className="radio-buttons">
                        {radio('leftAlignment', 'widgetHorizontalAlignment', styles.widgetHorizontalAlignment === 'left', () => set('widgetHorizontalAlignment', 'left'), 'left')}
                        {radio('rightAlignment', 'widgetHorizontalAlignment', styles.widgetHorizontalAlignment === 'right', () => set('widgetHorizontalAlignment', 'right'), 'right')}
                      </div>
                    </div></div>
                    <div className="control-group"><div className="color-picker-container">
                      <label className="widget-setting-title">{t('widget_vertical_alignment')}<FontAwesomeIcon icon={faArrowsUpDown} className="alignment-icon" /></label>
                      <div className="radio-buttons">
                        {radio('bottomAlignment', 'widgetVerticalAlignment', styles.widgetVerticalAlignment === 'bottom', () => set('widgetVerticalAlignment', 'bottom'), 'bottom')}
                        {radio('elevatedAlignment', 'widgetVerticalAlignment', styles.widgetVerticalAlignment === 'elevated', () => set('widgetVerticalAlignment', 'elevated'), 'elevated')}
                      </div>
                    </div></div>
                    <div className="control-group"><div className="color-picker-container">
                      <label className="widget-setting-title">{t('enable_pulsing')}</label>
                      <div className="radio-buttons">
                        {radio('pulsing-enabled', 'pulsing', styles.pulsing === true, () => set('pulsing', true), 'enabled')}
                        {radio('pulsing-disabled', 'pulsing', styles.pulsing === false, () => set('pulsing', false), 'disabled')}
                      </div>
                    </div></div>
                    <div className="control-group"><label><div className="border-radius-container">
                      <div className="widget-setting-title">{t('widget_size_label')}</div>
                      <div><input type="range" min="40" max="100" value={styles.widgetSize} onChange={(e) => set('widgetSize', parseInt(e.target.value, 10))} /></div>
                    </div></label></div>
                    <div className="control-group"><label><div className="border-radius-container">
                      <div className="widget-setting-title">{t('widget_border_radius_label')}</div>
                      <div><input type="range" min="0" max="50" value={styles.widgetBorderRadius} onChange={(e) => set('widgetBorderRadius', parseInt(e.target.value, 10))} /></div>
                    </div></label></div>
                    {uploadSlot('icon', 'widget_upload_icon_label', 'widget_upload_icon_tooltip', 'widget_existing_icon')}
                    {uploadSlot('logo', 'widget_upload_logo_label', 'widget_upload_logo_tooltip', 'widget_existing_logo')}
                    {uploadSlot('avatar', 'widget_upload_avatar_label', 'widget_upload_avatar_tooltip', 'widget_existing_avatar')}
                    <div className="control-group">
                      <FormControl sx={{ marginTop: '5px', maxWidth: '300px', width: '100%', '& label.Mui-focused': { color: '#5616ea' }, '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { border: '1px solid #515151' } } }}>
                        <InputLabel id="ac-font-family-select-label">{t('widget_font_family_label')}</InputLabel>
                        <Select labelId="ac-font-family-select-label" value={styles.fontFamily} onChange={(e) => set('fontFamily', e.target.value)} label={t('widget_font_family_label')}>
                          {fontOptions.map((o) => <MenuItem key={o.value} value={o.value} style={{ fontFamily: o.value }}>{o.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                      {!fontCfg && styles.fontFamily && <Typography variant="caption" color="textSecondary">{t('widget_using_custom_font', { font: styles.fontFamily })}</Typography>}
                    </div>
                  </AccordionDetails>
                </Accordion>

                {/* link behaviour */}
                <Accordion className="widget-accordion-last" expanded={!!expanded.linkBehavior} onChange={toggle('linkBehavior')} disableGutters elevation={0} sx={ACCORDION_SX}>
                  {summary('link_behavior', 'widget_link_behavior_tooltip')}
                  <AccordionDetails sx={ACCORDION_DETAILS_SX}>
                    <div className="control-group">
                      <FormControlLabel control={<Switch checked={!!styles.openLinksInNewTab} onChange={(e) => set('openLinksInNewTab', e.target.checked)} sx={SWITCH_SX} />} label={t('open_links_in_new_tab')} />
                    </div>
                  </AccordionDetails>
                </Accordion>
              </div>

              {err && <div className="ac-error">{err}</div>}
            </div>

            {/* ── RIGHT: static preview, real proportions, fills the column ── */}
            <div className="ac-design-preview">
              <ChatPreview s={styles} />
            </div>
          </div>
        )}
        {/* training strips: pinned under both columns so they are visible without scrolling */}
        {strips && <div className="ac-design-strips">{strips}</div>}
      </div>

      <StepFooter onBack={goBack} primaryLabel={t('ac_design_cta')} onPrimary={continueNext} busy={saving} />

      <CroppingModal isOpen={!!cropTarget} src={cropSrc} initialCrop={cropInitial} onCancel={cancelCrop} onConfirm={confirmCrop} />
      <PoweredByUpgradeModal open={brandingInfoOpen} onClose={() => setBrandingInfoOpen(false)} bot={agent}
        onUpgrade={() => { setBrandingInfoOpen(false); setPlanOpen(true); }} />
      <PlanModal open={planOpen} onClose={() => setPlanOpen(false)} agent={agent} returnPath={returnPath}
        title={t('ac_design_branding_modal_title')} subtitle={t('ac_design_branding_modal_sub')} />
    </>
  );
}
